import { useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Card } from '../../components/Card';
import { Input } from '../../catalyst/input';
import { Button } from '../../catalyst/button';
import { useFilterState } from '../../components/filters/useFilterState';
import { MembersGrid } from './MembersGrid';
import { MembersTable } from './MembersTable';
import type { ClusterResponse } from './insights';
import { PagePreviewHoverProvider } from '../../components/pagePreview/usePagePreviewHover';

type MemberFilters = { search: string; mismatchOnly: boolean; minScore: number };

const DEFAULTS: MemberFilters = { search: '', mismatchOnly: false, minScore: 0 };

export function MembersSection({
  pages,
  categoryName,
  selectedRows,
  setSelectedRows,
}: {
  pages: ClusterResponse['pages'];
  categoryName: string | undefined;
  selectedRows: Set<string>;
  setSelectedRows: Dispatch<SetStateAction<Set<string>>>;
}) {
  const [applied, setApplied] = useState<MemberFilters>(DEFAULTS);
  const { filters, setFilters, clear } = useFilterState<MemberFilters>({
    storageKey: 'category-cluster-members-filters',
    defaults: DEFAULTS,
    onFilterChange: setApplied,
  });
  const [view, setView] = useState<'grid' | 'table'>('grid');

  const filtered = useMemo(() => {
    const search = applied.search.toLowerCase();
    return pages.filter((p) => {
      if (applied.mismatchOnly && p.classificationCategory === categoryName) return false;
      if (applied.minScore > 0 && p.classificationScore < applied.minScore) return false;
      if (!search) return true;
      const title = p.classificationData?.title?.toLowerCase() ?? '';
      const label = (p.classificationCategory ?? '').toLowerCase();
      return p.id.toLowerCase().includes(search) || title.includes(search) || label.includes(search);
    });
  }, [pages, applied, categoryName]);

  // Visible (filtered) ids so the preview modal's prev/next walks only what's shown,
  // and so the hover popover dismisses when the hovered member leaves the set.
  const memberIds = filtered.map((p) => p.id);
  const visibleIds = useMemo(() => new Set(filtered.map((p) => p.id)), [filtered]);

  const toggle = (id: string) => {
    const next = new Set(selectedRows);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedRows(next);
  };

  // Select-all acts on the visible (filtered) set, leaving any selections outside
  // the current filter untouched. Once every visible member is selected it flips
  // to deselect-all for the same set.
  const allVisibleSelected = filtered.length > 0 && filtered.every((p) => selectedRows.has(p.id));
  const toggleAll = () => {
    const next = new Set(selectedRows);
    filtered.forEach((p) => (allVisibleSelected ? next.delete(p.id) : next.add(p.id)));
    setSelectedRows(next);
  };

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Members ({filtered.length})</h2>
            <button
              type="button"
              onClick={toggleAll}
              disabled={filtered.length === 0}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-40 dark:text-indigo-400"
            >
              {allVisibleSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          {/* Controls group: wraps as a unit, right-aligned via justify-between. */}
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Search id / title / label…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-56 text-sm"
            />
            <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-gray-700 dark:text-zinc-300">
              <input
                type="checkbox"
                checked={filters.mismatchOnly}
                onChange={(e) => setFilters({ ...filters, mismatchOnly: e.target.checked })}
              />
              Mismatch only
            </label>
            <label className="flex items-center gap-1.5 whitespace-nowrap text-sm text-gray-700 dark:text-zinc-300">
              Min score
              <Input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={filters.minScore}
                onChange={(e) => setFilters({ ...filters, minScore: Number(e.target.value) || 0 })}
                className="w-20 text-sm"
              />
            </label>
            <Button outline onClick={clear}>
              Clear
            </Button>
            {/* self-stretch matches the toggle height to the inputs/Clear button (the
                tallest siblings); the buttons center their label within it. */}
            <div className="flex self-stretch overflow-hidden rounded-md border border-gray-200 dark:border-zinc-700">
              <button
                type="button"
                onClick={() => setView('grid')}
                className={`flex items-center px-3 text-sm ${view === 'grid' ? 'bg-indigo-500 text-white' : 'text-gray-600 dark:text-zinc-300'}`}
              >
                Grid
              </button>
              <button
                type="button"
                onClick={() => setView('table')}
                className={`flex items-center px-3 text-sm ${view === 'table' ? 'bg-indigo-500 text-white' : 'text-gray-600 dark:text-zinc-300'}`}
              >
                Table
              </button>
            </div>
          </div>
        </div>

        <PagePreviewHoverProvider visibleIds={visibleIds} dismissKey={view}>
          {view === 'grid' ? (
            <MembersGrid pages={filtered} categoryName={categoryName} selectedRows={selectedRows} toggle={toggle} memberIds={memberIds} />
          ) : (
            <MembersTable pages={filtered} selectedRows={selectedRows} setSelectedRows={setSelectedRows} />
          )}
        </PagePreviewHoverProvider>
      </div>
    </Card>
  );
}
